"use client";

import { useMemo } from "react";
import type { Photo } from "@/lib/immich/types";
import {
  formatPhotoDayLabel,
  groupPhotosByDay,
  type PhotoDayGroup,
} from "@/lib/immich/dayGroups";
import { GalleryDaySection } from "./GalleryDaySection";
import { PhotoModal } from "./PhotoModal";
import {
  PhotoModalViewer,
  type PhotoModalViewerDownloadConfig,
  type PhotoModalViewerLabels,
} from "./PhotoModalViewer";
import { ShareButton, type ShareButtonLabels } from "./ShareButton";
import { useIsNarrowViewport } from "./useIsNarrowViewport";
import { usePhotoDeepLink } from "./usePhotoDeepLink";

export interface GalleryClientLabels extends PhotoModalViewerLabels {
  readonly close: string;
  readonly liveBadge: string;
  readonly videoBadge: string;
  /** Heading for the group of photos without any capture date. */
  readonly unknownDate: string;
}

export interface GalleryClientProps {
  readonly photos: readonly Photo[];
  readonly labels: GalleryClientLabels;
  /** Locale the day headings are formatted in, e.g. "nl". */
  readonly locale: string;
  readonly download?: PhotoModalViewerDownloadConfig;
  readonly share?: ShareButtonLabels;
  /** URL for the album with no photo open. Used to write/read path-based
   *  deep links, e.g. `/nl/photo-code/sh26`. */
  readonly basePath: string;
  /** If set, opens the modal at this photo on initial render. Usually
   *  derived server-side from the second path segment. */
  readonly initialPhotoId?: string;
}

interface GallerySection {
  readonly group: PhotoDayGroup;
  readonly heading: string | null;
}

export function GalleryClient({
  photos,
  labels,
  locale,
  download,
  share,
  basePath,
  initialPhotoId,
}: GalleryClientProps) {
  const { openIndex, isOpen, handleOpen, handleClose, handleIndexChange } =
    usePhotoDeepLink(photos, basePath, initialPhotoId);
  const isNarrow = useIsNarrowViewport();

  const sections = useMemo<readonly GallerySection[]>(() => {
    const groups = groupPhotosByDay(photos);
    // A gallery that is one single undated group has nothing to
    // distinguish; render it exactly as before, without a heading.
    const showHeadings =
      groups.length > 1 || groups.some((g) => g.day !== null);
    return groups.map((group) => ({
      group,
      heading: !showHeadings
        ? null
        : group.day !== null
          ? formatPhotoDayLabel(group.day, locale)
          : labels.unknownDate,
    }));
  }, [photos, locale, labels.unknownDate]);

  const viewerLabels = useMemo<PhotoModalViewerLabels>(
    () => ({
      previous: labels.previous,
      next: labels.next,
      imageFailed: labels.imageFailed,
      liveLoop: labels.liveLoop,
      liveStopLoop: labels.liveStopLoop,
    }),
    [
      labels.previous,
      labels.next,
      labels.imageFailed,
      labels.liveLoop,
      labels.liveStopLoop,
    ],
  );

  return (
    <>
      {sections.map(({ group, heading }) => (
        <GalleryDaySection
          key={group.items[0].photo.id}
          group={group}
          heading={heading}
          isNarrow={isNarrow}
          onOpen={handleOpen}
          liveBadgeLabel={labels.liveBadge}
          videoBadgeLabel={labels.videoBadge}
        />
      ))}
      <PhotoModal
        isOpen={isOpen}
        onClose={handleClose}
        ariaLabel={labels.close}
      >
        {openIndex !== null ? (
          <PhotoModalViewer
            photos={photos}
            index={openIndex}
            onIndexChange={handleIndexChange}
            labels={viewerLabels}
            download={download}
            toolbar={share ? <ShareButton labels={share} /> : null}
          />
        ) : null}
      </PhotoModal>
    </>
  );
}
